import { useMemo } from 'react'
import { useQuery } from 'react-query'
import {
  DetailPanel,
  InfiniteCarousel,
  PieChart,
  StatCards,
  TableCard,
  TableEmptyState,
  TableErrorState,
} from '../../../../../../components'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { NPSType, showError } from '../../../../../../../_metronic/helpers'
import { getComentariosNps } from '../../core/_requests'
import { NPSData } from '../../core/_models'
import styles from './styles/RelatorioNpsData.module.css'

/** Proporção (0–1) como porcentagem pt-BR, com até 2 casas. */
const porcentagem = (valor?: number) =>
  `${Number(((valor ?? 0) * 100).toFixed(2)).toLocaleString('pt-BR')}%`

/** Linhas da tabela de cada categoria: contagens e, depois de um respiro, as porcentagens. */
const CONTAGENS = [
  'Resposta 1',
  'Resposta 2',
  'Resposta 3',
  'Resposta 4',
  'Resposta 5',
  'Total',
  'Não respondido',
] as const
const PORCENTAGENS = [
  'Porcentagem de Promotores',
  'Porcentagem de Neutros',
  'Porcentagem de Detratores',
  'NPS',
] as const

/**
 * Resumo NPS em cards (um por categoria): gráfico de promotores/neutros/detratores e a tabela de
 * respostas e porcentagens. Abaixo, os comentários deixados pelos pacientes.
 */
export const RelatorioNpsData = () => {
  const { responseSemPaginacao: resumo, isLoading, isError, refetch, query } = useQueryResponse()

  const { data: comentarios } = useQuery(
    ['nps-comentarios', query],
    () => getComentariosNps(query),
    { keepPreviousData: true, refetchOnWindowFocus: false, onError: showError }
  )

  const categorias = useMemo(
    () =>
      Object.entries(resumo ?? {}).map(([categoria, valores]: [string, NPSData]) => ({
        label: categoria === 'Uso do Telekit' ? 'Uso da Tecnologia' : categoria,
        valores,
        series: {
          [NPSType.Detratores]: valores?.Detratores || 0,
          [NPSType.Neutros]: valores?.Neutros || 0,
          [NPSType.Promotores]: valores?.Promotores || 0,
        },
      })),
    [resumo]
  )

  if (isError) {
    return (
      <TableCard>
        <TableErrorState onRetry={refetch} />
      </TableCard>
    )
  }

  if (!resumo) {
    return (
      <TableCard>
        {isLoading ? (
          <DetailPanel isLoading loadingLabel='Carregando o resumo NPS…' />
        ) : (
          <TableEmptyState
            title='Nenhuma avaliação encontrada'
            description='Revise os filtros aplicados.'
          />
        )}
      </TableCard>
    )
  }

  return (
    <>
      <div className='row g-6 mb-6' aria-busy={isLoading}>
        {categorias.map(({ label, valores, series }) => (
          <div className='col-lg-4 col-md-12' key={label}>
            <TableCard className='h-100'>
              <div className='card-body'>
                <PieChart label={label} series={series} />
                <div className='table-responsive mt-4'>
                  <table className='table table-striped table-bordered mb-0'>
                    <thead>
                      <tr>
                        <th scope='col' className='text-center'>
                          Descrição
                        </th>
                        <th scope='col' className='text-center'>
                          Valor
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {CONTAGENS.map((chave) => (
                        <tr key={chave}>
                          <th scope='row'>{chave}</th>
                          <td className='text-center'>{valores[chave]}</td>
                        </tr>
                      ))}
                      {/* Respiro entre as contagens e as porcentagens, como antes. */}
                      <tr aria-hidden='true'>
                        <td colSpan={2} style={{ height: '30px', background: 'transparent' }} />
                      </tr>
                      {PORCENTAGENS.map((chave) => (
                        <tr key={chave}>
                          <th scope='row'>{chave}</th>
                          <td className='text-center'>{porcentagem(valores[chave])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TableCard>
          </div>
        ))}
      </div>

      <TableCard>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <h3 className='card-label fw-bold text-gray-800'>Comentários</h3>
          </div>
        </div>
        <div className='card-body pt-2'>
          {comentarios?.length ? (
            <InfiniteCarousel
              items={comentarios}
              visibleItems={3}
              renderItem={(texto) => (
                <div className={styles.comentario}>
                  <StatCards
                    items={[
                      {
                        label: 'Comentário',
                        value: <span className='fs-6 fw-normal fst-italic'>“{texto}”</span>,
                      },
                    ]}
                  />
                </div>
              )}
            />
          ) : (
            <TableEmptyState
              title='Nenhum comentário'
              description='Os comentários deixados pelos pacientes nas avaliações aparecem aqui.'
            />
          )}
        </div>
      </TableCard>
    </>
  )
}
